<script setup lang="ts">
import { classname } from '@/js/utils.js';
import { ref, watch } from 'vue';

const props = defineProps<{
  modelValue: boolean
  disabled?: boolean
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>();

const classBaseCheckbox = classname('vue-base-checkbox');

const checked = ref(props.modelValue);

watch(() => props.modelValue, (val) => {
  checked.value = val;
});

watch(checked, (val) => {
  emit('update:modelValue', val);
});
</script>

<template>
  <label :class="classBaseCheckbox()">
    <input
      v-model="checked"
      :disabled="disabled"
      :class="classBaseCheckbox('input')"
      type="checkbox"
    >
    <slot />
  </label>
</template>

<style lang="scss">
.vue-base-checkbox {
  --border: #c4c4c4;
  --focus: #3475dd4c;
  --active: #3962b2;
  --disabled: #f6f8ff;
  --border-hover: #3962b2;
  --check-mark: #fff;

  display: inline-flex;
  grid-gap: 8px;
  align-items: center;
  margin: 0;
  color: #000;
  font-weight: 300;
  font-size: 12px;
  line-height: 1;

  &__input {
    position: relative;
    display: inline-block;
    flex-shrink: 0;
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    margin: 0;
    border: 1px solid var(--bc, var(--border));
    border-radius: 1px;
    transition:
      background 0.3s,
      border-color 0.3s,
      box-shadow 0.2s;
    -webkit-appearance: none;
    -moz-appearance: none;

    &:focus {
      outline-color: var(--focus);

      &::placeholder {
        opacity: 0.7;
      }
    }

    &::after {
      position: absolute;
      top: 1px;
      left: 1px;
      display: block;
      transition:
        transform var(--d-t, 0.3s) var(--d-t-e, ease),
        opacity var(--d-o, 0.2s);
      content: '';
    }

    &:checked {
      --border: var(--active);
      --d-o: 0.3s;
      --d-t: 0.6s;
      --d-t-e: cubic-bezier(0.2, 0.85, 0.32, 1.2);
    }

    &:hover {
      &:not(:disabled) {
        --bc: var(--border-hover);
      }
    }

    &[type='checkbox'] {
      &::after {
        top: 1px;
        left: 6px;
        width: 6px;
        height: 11px;
        border: 1.7px solid var(--check-mark);
        border-top: 0;
        border-left: 0;
        border-radius: 1px;
        transform: rotate(var(--r, 20deg));
      }

      &:checked {
        background-color: var(--active);

        --r: 43deg;
      }
    }
  }

  &:has(:disabled) {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &_hide {
    &_input {
      .vue-base-checkbox__input {
        display: none;
      }
    }
  }
}
</style>
